import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, apiError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquare, X, Send, Link2, Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLiveSync } from "@/hooks/useLiveSync";

const QUICK = ["Sur place", "Retard 10 min", "Patient absent", "Patient chargé", "Arrivé destination", "Appelez-moi"];
const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "");

export function DriverChat({ rides = [] }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [unread, setUnread] = useState(0);
  const [rideId, setRideId] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const endRef = useRef(null);

  const loadUnread = useCallback(() => {
    api.get("/chat/unread").then((r) => setUnread(r.data.unread || 0)).catch(() => {});
  }, []);
  const loadMessages = useCallback(() => {
    api.get("/chat/thread/me").then((r) => setMessages(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadUnread(); }, [loadUnread]);

  useLiveSync((evt) => {
    if (evt?.type !== "chat_updated") return;
    loadUnread();
    if (open) loadMessages();
  });

  useEffect(() => {
    if (!open) return;
    loadMessages();
    api.post("/chat/thread/me/read").then(() => setUnread(0)).catch(() => {});
  }, [open, loadMessages]);

  useEffect(() => { if (open) endRef.current?.scrollIntoView({ block: "end" }); }, [messages, open]);

  const send = async (value) => {
    const content = (value ?? text).trim();
    if (!content) return;
    if (!value) setText("");
    try {
      await api.post("/chat/thread/me", { text: content, ride_id: rideId || null });
      loadMessages();
    } catch (err) { toast.error(apiError(err)); }
  };

  const sendPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/chat/thread/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo envoyée au régulateur");
      loadMessages();
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <>
      <Button variant="ghost" size="icon" className="relative" onClick={() => setOpen(true)} data-testid="driver-chat-open">
        <MessageSquare className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="driver-chat-badge">
            {unread}
          </span>
        )}
      </Button>

      {open && (
        <div className="fixed inset-0 z-50 bg-background flex flex-col" data-testid="driver-chat-panel">
          <header className="h-14 px-3 flex items-center gap-2 border-b border-border shrink-0">
            <div>
              <p className="font-semibold text-sm">Régulation</p>
              <p className="text-[11px] text-muted-foreground">Messagerie interne</p>
            </div>
            <Button variant="ghost" size="icon" className="ml-auto" onClick={() => setOpen(false)} data-testid="driver-chat-close">
              <X className="h-5 w-5" />
            </Button>
          </header>

          <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="driver-chat-messages">
            {messages.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Aucun message pour le moment.</p>}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender === "driver" ? "justify-end" : "justify-start"}`} data-testid={`driver-chat-msg-${m.id}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${m.sender === "driver" ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-secondary text-foreground rounded-bl-sm"}`}>
                  {m.ride_label && (
                    <span className={`block text-[10px] mb-1 flex items-center gap-1 ${m.sender === "driver" ? "opacity-80" : "text-muted-foreground"}`}>
                      <Link2 className="h-3 w-3" /> {m.ride_label}
                    </span>
                  )}
                  {m.photo_doc_id ? (
                    <a href={`${API_BASE}/chat/photo/${m.photo_doc_id}`} target="_blank" rel="noreferrer" data-testid={`driver-chat-photo-${m.id}`}>
                      <img src={`${API_BASE}/chat/photo/${m.photo_doc_id}`} alt="Pièce jointe" className="rounded-lg max-h-56 w-auto" />
                    </a>
                  ) : (
                    <span className="whitespace-pre-wrap break-words">{m.text}</span>
                  )}
                  <span className={`block text-[10px] mt-0.5 text-right ${m.sender === "driver" ? "opacity-70" : "text-muted-foreground"}`}>{hhmm(m.created_at)}</span>
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>

          <div className="border-t border-border p-2 space-y-2 shrink-0">
            <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="driver-chat-quick">
              {QUICK.map((q) => (
                <Button key={q} type="button" variant="outline" size="sm" className="text-xs whitespace-nowrap shrink-0"
                  onClick={() => send(q)} data-testid={`driver-chat-quick-${q.replace(/\s+/g, "-").toLowerCase()}`}>
                  {q}
                </Button>
              ))}
            </div>
            {rides.length > 0 && (
              <select
                value={rideId}
                onChange={(e) => setRideId(e.target.value)}
                className="w-full h-8 rounded-md border border-input bg-background px-2 text-xs"
                data-testid="driver-chat-ride"
              >
                <option value="">Aucune course citée</option>
                {rides.map((r) => (
                  <option key={r.id} value={r.id}>{`${r.pec_time || r.time} · ${r.patient_name}`}</option>
                ))}
              </select>
            )}
            <div className="flex gap-2">
              <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
                onChange={(e) => sendPhoto(e.target.files?.[0])} data-testid="driver-chat-file" />
              <Button variant="outline" size="icon" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="driver-chat-photo-btn">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
              </Button>
              <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
                placeholder="Votre message…"
                data-testid="driver-chat-input"
              />
              <Button size="icon" onClick={() => send()} disabled={!text.trim()} data-testid="driver-chat-send">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
